// utils/categoryTranslation.js
import { useTranslation } from 'react-i18next';

/**
 * Hook personnalisé pour obtenir le nom traduit d'une catégorie
 * @param {Object} category - Objet catégorie avec slug et name
 * @returns {string} - Nom traduit de la catégorie
 */
export const useCategoryName = () => {
  const { t } = useTranslation('categories');
  
  return (category) => {
    if (!category) return '';
    const categoryKey = `category_${category.slug}`;
    const raw = t(categoryKey, { defaultValue: category.name ?? '' });
    // Toujours retourner une string — si i18next renvoie un objet (namespace non chargé,
    // returnObjects accidentel, etc.) on coerce pour éviter l'erreur React.
    if (raw === null || raw === undefined) return String(category.name ?? '');
    if (typeof raw === 'object') return String(category.name ?? category.slug ?? '');
    return String(raw);
  };
};

/**
 * Hook personnalisé pour obtenir la description traduite d'une catégorie
 * @param {Object} category - Objet catégorie avec slug et description
 * @returns {string} - Description traduite de la catégorie
 */
export const useCategoryDescription = () => {
  const { t } = useTranslation('categories');

  return (category) => {
    if (!category) return '';
    const categoryKey = `category_${category.slug}_description`;
    const raw = t(categoryKey, { defaultValue: category.description || '' });
    if (raw === null || raw === undefined) return '';
    if (typeof raw === 'object') return String(category.description || '');
    return String(raw);
  };
};

/**
 * Fonction utilitaire pour obtenir le nom traduit d'une catégorie (version non-hook)
 * @param {Function} t - Fonction de traduction i18n
 * @param {Object} category - Objet catégorie avec slug et name
 * @returns {string} - Nom traduit de la catégorie
 */
export const getCategoryName = (t, category) => {
  if (!category) return '';
  
  const categoryKey = `category_${category.slug}`;
  return t(categoryKey, { defaultValue: category.name, ns: 'categories' });
};

/**
 * Fonction utilitaire pour obtenir la description traduite d'une catégorie (version non-hook)
 * @param {Function} t - Fonction de traduction i18n
 * @param {Object} category - Objet catégorie avec slug et description
 * @returns {string} - Description traduite de la catégorie
 */
export const getCategoryDescription = (t, category) => {
  if (!category) return '';
  const categoryKey = `category_${category.slug}_description`;
  return t(categoryKey, { defaultValue: category.description || '', ns: 'categories' });
};
